import React, { useContext, useEffect, useState, Fragment, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import Badge from '../../shared/TailwindComponents/Badge';
import { useBreadcrumbs } from '../../../contexts/BreadcrumbsContext';
import { useAppDispatch, RootState } from '../../../store';
import { showFeedbackModal } from '../../../store/actions/UserFeedbackActions';
import apis from '../../../utils/apis';
import { motion } from 'framer-motion';
import { useSelector } from 'react-redux';
import {
  PiPencilLight,
  PiDotsThreeVerticalLight,
  PiCheckCircleLight,
} from 'react-icons/pi';
import { formatRole, formatDateTime } from '../../../utils/functions';
import SkeletonLoader from './SkeletonLoader';
import { LayoutContext } from '../../../contexts/LayoutContext';
import { useTranslation } from 'react-i18next';
import Button from '../../shared/TailwindComponents/Button';
import ProfileImage from '../../Header/ProfileImage';
import { Menu, Transition } from '@headlessui/react';
import DetailFrame from '../../shared/Frames/DetailFrame';
import { useFeatureTour } from '../../../hooks/useFeatureTour';
import { FeatureTour, peopleDetailTourSteps } from '../../shared/FeatureTour';

interface EmployeeDetailParams {
  employeeId: string;
}

interface ProfileData {
  availability_status?: string;
  bio?: string;
  education?: any[];
  experience_years?: number;
  experiences?: any[];
  fixed_working_hours?: number;
  hourly_rate?: number;
  languages?: any[];
  languages_spoken?: string[];
  license_document_link?: string;
  practice_areas?: any[];
  professional_memberships?: string[];
  specializations?: string[];
  updated_at?: string;
  profile_picture?: string;
}

interface UserInfo {
  email: string;
  first_name: string;
  id: string;
  last_name: string;
  role: string;
}

interface ColleagueProfileData {
  profile: ProfileData;
  user_info: UserInfo;
  success?: boolean;
}

const EmployeeDetail: React.FC = () => {
  const { t } = useTranslation();
  const { employeeId } = useParams<EmployeeDetailParams>();
  const dispatch = useAppDispatch();
  const { setBreadcrumbs } = useBreadcrumbs();
  const { setIsContentScrollable } = useContext(LayoutContext);
  const history = useHistory();
  const { subscriptionActive } = useSelector((state: RootState) => state.subscription);

  const [profileData, setProfileData] = useState<ColleagueProfileData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  const reduxUserInfo = useSelector((state: RootState) => state.user?.userInfo);

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'people-detail',
    steps: peopleDetailTourSteps,
    readyToShow: !loading && !!profileData,
    autoStartDelay: 600,
  });

  const isOwnProfile = useMemo(() => {
    if (!profileData?.user_info || !reduxUserInfo) return false;
    return profileData.user_info.id === reduxUserInfo.UserUID;
  }, [profileData?.user_info, reduxUserInfo]);

  const subHeaderRight = useMemo(() => {
    if (!profileData) return undefined;
    const { profile, user_info } = profileData;
    const availabilityColor = profile.availability_status === 'available' ? 'green' : profile.availability_status === 'on_leave' ? 'yellow' : 'red';
    return (
      <div className="flex flex-row gap-2 items-center">
        <Badge color={availabilityColor}>
          {profile.availability_status?.replace(/_/g, ' ').toUpperCase() || 'Not Set'}
        </Badge>
        <Badge color="gray">
          {formatRole(user_info.role)}
        </Badge>
      </div>
    );
  }, [profileData]);

  useEffect(() => {
    setIsContentScrollable(false);
    return () => setIsContentScrollable(true);
  }, [setIsContentScrollable]);

  useEffect(() => {
    if (!employeeId) return;

    let isMounted = true;

    const loadProfileData = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await apis.get(`/user-profile/user/uuid/${employeeId}`);
        if (isMounted) {
          if (response?.data?.success && response.data?.profile && response.data?.user_info) {
            setProfileData(response.data);
          } else {
            throw new Error('Invalid response format');
          }
        }
      } catch (err: any) {
        if (isMounted) {
          console.error('[ERROR] Error loading profile data:', err);
          const errorMsg = err?.response?.data?.message || t('Failed to load profile');
          setError(errorMsg);
          dispatch(
            showFeedbackModal({
              showModal: true,
              message: errorMsg,
              modalType: 'error',
              duration: 3000,
            })
          );
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadProfileData();

    return () => {
      isMounted = false;
    };
  }, [employeeId]);



  useEffect(() => {
    if (profileData?.user_info) {
      const fullName = `${profileData.user_info.first_name} ${profileData.user_info.last_name}`.trim();
      setBreadcrumbs([
        { name: t('People'), href: '/people', current: false },
        { name: fullName, href: `/people/${employeeId}`, current: true },
      ]);
    } else {
      setBreadcrumbs([
        { name: t('People'), href: '/people', current: false },
        { name: t('Loading'), href: `/people/${employeeId}`, current: true },
      ]);
    }
    return () => setBreadcrumbs([]);
  }, [profileData, employeeId, setBreadcrumbs]);



  const additionalHeaderElements = (
    <div className="flex items-center gap-2">
      {isOwnProfile && (
        <Button
          variant="primary"
          size="small"
          onClick={() => history.push('/account?tab=personal')}
        >
          <PiPencilLight className="h-4 w-4 mr-2" />
          {t('Edit Profile')}
        </Button>
      )}
      {/* <Menu as="div" className="relative inline-block text-left z-50">
        <Menu.Button as={Button} variant="neutral" size="small" className="p-1">
          <PiDotsThreeVerticalLight className="h-4 w-4" />
        </Menu.Button>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="absolute right-0 mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none" />
        </Transition>
      </Menu> */}
    </div>
  );

  if (loading) {
    return (
      <DetailFrame
        breadcrumbs={[]}
        dropdownActions={[]}
        subscriptionActive={subscriptionActive}
        additionalHeaderElements={additionalHeaderElements}
        showBackButton={true}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="p-6"
        >
          <SkeletonLoader />
        </motion.div>
      </DetailFrame>
    );
  }

  if (error || !profileData) {
    return (
      <DetailFrame
        breadcrumbs={[]}
        dropdownActions={[]}
        subscriptionActive={subscriptionActive}
        additionalHeaderElements={additionalHeaderElements}
        showBackButton={true}
      >
        <div className="text-red-600 dark:text-red-400 p-4 text-center">
          {error || t('Failed to load profile')}
        </div>
      </DetailFrame>
    );
  }

  const { profile, user_info } = profileData;
  const displayName = `${user_info.first_name} ${user_info.last_name}`.trim();
  const initials = `${user_info.first_name?.[0] || ''}${user_info.last_name?.[0] || ''}`.toUpperCase();



  return (
    <DetailFrame
      breadcrumbs={[
        { name: t('People'), href: '/people', current: false },
        { name: displayName, href: `/people/${employeeId}`, current: true },
      ]}
      dropdownActions={[]}
      subscriptionActive={subscriptionActive}
      additionalHeaderElements={additionalHeaderElements}
      subHeaderRight={subHeaderRight}
      showBackButton={true}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex flex-col h-full w-full bg-transparent dark:bg-transparent"
      >
        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar-main pb-6">
          {/* Header */}
          <div data-tour-id="people-detail-header" className="pt-6 pb-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-start mb-6 px-6">
            <div className="flex gap-4 items-start">
              <div className="flex-shrink-0">
                <ProfileImage
                  profilePicture={profile.profile_picture || null}
                  email={user_info.email}
                  initials={initials}
                  firstName={user_info.first_name}
                  lastName={user_info.last_name}
                  size="w-16 h-16"
                />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{displayName}</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">{user_info.email}</p>
                {user_info.role && (
                  <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                    {formatRole(user_info.role)}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Two Column Layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 px-6">
            {/* Left Column - Personal Information */}
            <div data-tour-id="people-detail-personal" className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 border-b border-gray-200 dark:border-gray-700 pb-3">
                  {t('Personal Information')}
                </h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-normal text-gray-700 dark:text-gray-300 mb-2">
                        {t('First Name')}
                      </label>
                      <div className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md text-xs text-gray-900 dark:text-gray-100">
                        {user_info.first_name || '-'}
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-normal text-gray-700 dark:text-gray-300 mb-2">
                        {t('Last Name')}
                      </label>
                      <div className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md text-xs text-gray-900 dark:text-gray-100">
                        {user_info.last_name || '-'}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-normal text-gray-700 dark:text-gray-300 mb-2">
                        {t('Hourly Rate ($)')}
                      </label>
                      <div className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md text-xs text-gray-900 dark:text-gray-100">
                        {profile.hourly_rate ? `$${profile.hourly_rate}` : '-'}
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-normal text-gray-700 dark:text-gray-300 mb-2">
                        {t('Years of Experience')}
                      </label>
                      <div className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md text-xs text-gray-900 dark:text-gray-100">
                        {profile.experience_years ? `${profile.experience_years}` : '-'}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-normal text-gray-700 dark:text-gray-300 mb-2">
                        {t('Fixed Working Hours/Week')}
                      </label>
                      <div className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md text-xs text-gray-900 dark:text-gray-100">
                        {profile.fixed_working_hours ? `${profile.fixed_working_hours}` : '-'}
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-normal text-gray-700 dark:text-gray-300 mb-2">
                        {t('Availability Status')}
                      </label>
                      <div className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md text-xs text-gray-900 dark:text-gray-100">
                        {profile.availability_status ? profile.availability_status.replace(/_/g, ' ').charAt(0).toUpperCase() + profile.availability_status.replace(/_/g, ' ').slice(1) : '-'}
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-normal text-gray-700 dark:text-gray-300 mb-2">
                      {t('Languages Spoken')}
                    </label>
                    <div className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md text-xs text-gray-900 dark:text-gray-100">
                      {profile.languages_spoken && profile.languages_spoken.length > 0
                        ? profile.languages_spoken.join(', ')
                        : '-'}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Professional Summary */}
            <div data-tour-id="people-detail-professional" className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 border-b border-gray-200 dark:border-gray-700 pb-3">
                  {t('Professional Summary')}
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-normal text-gray-700 dark:text-gray-300 mb-2">
                      {t('Bio')}
                    </label>
                    <div className="w-full min-h-32 px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md text-xs text-gray-900 dark:text-gray-100 whitespace-pre-wrap break-words">
                      {profile.bio || '-'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-normal text-gray-700 dark:text-gray-300 mb-2">
                      {t('Specializations')}
                    </label>
                    <div className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md text-xs text-gray-900 dark:text-gray-100">
                      {profile.specializations && profile.specializations.length > 0
                        ? profile.specializations.join(', ')
                        : '-'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-normal text-gray-700 dark:text-gray-300 mb-2">
                      {t('Professional Memberships')}
                    </label>
                    <div className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md text-xs text-gray-900 dark:text-gray-100">
                      {profile.professional_memberships && profile.professional_memberships.length > 0
                        ? profile.professional_memberships.join(', ')
                        : '-'}
                    </div>
                  </div>

                  {profile.updated_at && (
                    <div className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg mt-6">
                      <PiCheckCircleLight className="text-green-600 h-5 w-5 flex-shrink-0" />
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {t('Last updated')}: {formatDateTime(profile.updated_at)}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
      <FeatureTour
        steps={peopleDetailTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        continuous
        showProgress
        showSkipButton
        disableOverlay
      />
    </DetailFrame>
  );
};

export default EmployeeDetail;
